/**
 * Storm translation: a uniform steering flow that carries the vortex.
 *
 *   center(t) = center0 + steer * t
 *   u_total(x, t) = u_vortex(x - center(t)) + steer
 *
 * Adding a uniform flow to the rotating field makes the wind stronger on
 * the side where rotation and motion point the same way: the right of the
 * track in the Northern Hemisphere, the left in the Southern. No separate
 * asymmetry term is needed. A uniform flow has zero divergence.
 *
 * Heading is a compass bearing (0 = toward north, 90 = toward east). With
 * x east and z south: steer = speed * (sin(heading), 0, -cos(heading)).
 */

export interface StormTranslationParams {
  /** Steering flow, m/s, east component. */
  steerXMps: number;
  /** Steering flow, m/s, south component. */
  steerZMps: number;
  /** Center at t = 0, m. */
  originXM: number;
  originZM: number;
}

export function createTranslationParams(): StormTranslationParams {
  return { steerXMps: 0, steerZMps: 0, originXM: 0, originZM: 0 };
}

/** Set steering from speed (m/s) and compass heading (degrees, direction of travel). */
export function setSteering(
  p: StormTranslationParams,
  speedMps: number,
  headingDeg: number,
): StormTranslationParams {
  const h = (headingDeg * Math.PI) / 180;
  p.steerXMps = speedMps * Math.sin(h);
  p.steerZMps = -speedMps * Math.cos(h);
  return p;
}

/** Add the steering vector to `out`. */
export function addTranslation<T extends { x: number; z: number }>(
  p: StormTranslationParams,
  out: T,
): T {
  out.x += p.steerXMps;
  out.z += p.steerZMps;
  return out;
}

/** Storm center at time tS, written to out.x and out.z. */
export function stormCenterAt<T extends { x: number; z: number }>(
  p: StormTranslationParams,
  tS: number,
  out: T,
): T {
  out.x = p.originXM + p.steerXMps * tS;
  out.z = p.originZM + p.steerZMps * tS;
  return out;
}
